<template>
  <ModalDialog
    ref="modal"
    data-testid="send-reminder-dialog"
    persistent
    max-height="95vh"
    max-width="648px"
    :title="sendReminderDialogTexts?.modalTitle"
    @closed="$emit('closed')"
  >
    <div class="mb-6 text-body-s">{{ sendReminderDialogTexts?.modalDescription }}</div>
    <MessageBanner
      v-if="messageBannerTexts"
      class="my-6"
      :headline="messageBannerTexts.headline"
      :message="messageBannerTexts.text"
      :type="messageBannerTexts.type"
    />
    <div v-if="!isBulkReminder">
      <div class="mb-1.5 text-label-m">
        {{ t('Reviews.sendReminderDialog.recipientLabel') }}
      </div>
      <ChipBadge v-if="reviewerFirstName" class="mb-4" rounded> {{ reviewerFirstName }} </ChipBadge>
      <div v-else>{{ t('Reviews.sendReminderDialog.salutationFallback') }}</div>
    </div>
    <div class="mb-1.5 flex items-end justify-between gap-2 text-label-m">
      <span>{{ t('Reviews.sendReminderDialog.messageLabel') }}</span>
      <div class="flex items-center gap-2">
        <div class="w-72 flex-shrink-0">
          <RichSelectField
            id="send-reminder-campaign-select"
            :model-value="selectedCampaignHash ?? undefined"
            data-testid="send-reminder-campaign-select"
            size="s"
            clearable
            :disabled="!hasCampaignOptions"
            :placeholder="
              hasCampaignOptions
                ? t('Reviews.sendReminderDialog.campaignSelect.placeholder')
                : t('Reviews.sendReminderDialog.campaignSelect.noActiveCampaigns')
            "
            max-height="20rem"
            @update:model-value="onCampaignSelectionChange"
          >
            <template #selected>
              <span class="truncate">
                {{
                  t('Reviews.sendReminderDialog.campaignSelect.selectedSummary', {
                    campaignTitle: selectedCampaignTitle,
                    locale: selectedPersonalization?.locale ?? '',
                  })
                }}
              </span>
            </template>
            <template v-for="group in campaignGroups" :key="group.id">
              <div class="bg-surface-elevation-1 px-3 py-1 txt-label-s-semibold text-subtle">
                {{ group.title }}
              </div>
              <RichSelectOption
                v-for="personalization in group.personalizations"
                :key="personalization.hash"
                :value="personalization.hash"
                :label="personalization.inviteMessage"
              >
                <div class="relative pr-10">
                  <ChipBadge rounded size="s" class="absolute -top-3 right-0">
                    {{ personalization.locale }}
                  </ChipBadge>
                  <span class="block whitespace-normal">{{ personalization.inviteMessage }}</span>
                </div>
              </RichSelectOption>
            </template>
          </RichSelectField>
        </div>
        <button v-if="focused" class="btn-outline-neutral-xs" @mousedown.prevent @click="insertLink">
          {{ t('Reviews.sendReminderDialog.insertReviewLink') }}
        </button>
        <TooltipWrapper v-else :z-index="60">
          <template #trigger>
            <button class="btn-outline-neutral-xs" disabled @click="insertLink">
              {{ t('Reviews.sendReminderDialog.insertReviewLink') }}
            </button>
          </template>
          <template #content>
            <div class="max-w-xs" v-html="t('Reviews.sendReminderDialog.insertReviewLinkHint')" />
          </template>
        </TooltipWrapper>
      </div>
    </div>
    <RichInput
      id="message-input"
      ref="richInput"
      :key="inputKey"
      v-model="v$.message.$model"
      :error="getValidationErrors(v$.message)"
      hide-controls
      plain-text-only
      @focusin="onFocusIn"
      @focusout="onFocusOut"
    />
    <div class="flex items-center justify-between">
      <CheckBox v-model="v$.copyToSender.$model">{{ t('Reviews.sendReminderDialog.copyToSender') }}</CheckBox>
      <button class="btn-primary-color-s" data-testid="reviews-reminder-submit" :disabled="loading || v$.$invalid" @click="onSubmit">
        {{ isBulkReminder ? t('Reviews.sendReminderDialog.sendBulkReminder') : t('Reviews.sendReminderDialog.send') }}
      </button>
    </div>
  </ModalDialog>
</template>
<script setup lang="ts">
import {
  ChipBadge,
  CheckBox,
  ModalDialog,
  RichSelectField,
  RichSelectOption,
  TooltipWrapper,
  nl2br,
  MessageBanner,
} from '@ramp106/omrjs-core-ui'
import { RichInput } from '@ramp106/omrjs-core-ui/deprecated'
import { track } from '@ramp106/omrjs-tracking-reviews'
import { useVuelidate, type ValidationArgs } from '@vuelidate/core'
import { storeToRefs } from 'pinia'
import TurnDown from 'turndown'
import { computed, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import type { SurveyResponse } from '@/components/ReviewsManagement/Reviews/types.ts'
import { SurveyResponseState, useSurveyResponseState } from '@/composables/useSurveyResponseState.ts'
import {
  type ProductById,
  type ManageSurveyResponsesSearchFilters,
  IncentivizationCampaignStatusEnum,
  useCreateSurveyResponseReminderMutation,
  useCreateSurveyResponseRemindersMutation,
  useReviewsIncentivizationCampaignsQuery,
} from '@/gql/reviews'
import { showNotification } from '@/helpers/notificationHelper'
import { getValidationErrors } from '@/helpers/validationHelper'
import { required, maxLength, minLength, containsValidator, withI18nMessage } from '@/services/validators'
import { useMeStore } from '@/stores'

import getMessageBannerForReviewState from './getMessageBannerForReviewState'

type FormData = {
  message: string
  copyToSender: boolean
}

const MSG_MIN_LENGTH = 100
const MSG_MAX_LENGTH = 10000
const REVIEW_LINK_BE_PLACEHOLDER = '%{review_link}'
const REVIEW_LINK_PLACEHOLDER = `(${REVIEW_LINK_BE_PLACEHOLDER})`

const props = defineProps<{
  surveyResponse?: Pick<SurveyResponse, 'id' | 'reviewerFirstName' | 'aasmState' | 'rejectedReason'>
  product: Pick<ProductById, 'id' | 'title' | 'slug'>
  hasOldReview?: boolean
  isBulkReminder?: boolean
  filters?: ManageSurveyResponsesSearchFilters
}>()

defineEmits(['closed'])

const meStore = useMeStore()
const { me } = storeToRefs(meStore)
const modal = ref<InstanceType<typeof ModalDialog> | null>(null)
const richInput = ref<InstanceType<typeof RichInput> | null>(null)
const inputKey = ref(1)
const focused = ref(false)
const { t } = useI18n()
const state = props.surveyResponse ? useSurveyResponseState(props.surveyResponse) : undefined
const reviewerFirstName = computed(() => props.surveyResponse?.reviewerFirstName || undefined)
const reminderType = computed(() => (props.isBulkReminder || props.hasOldReview ? 'invite_to_update' : 'invite_to_complete'))

const { loading: singleReminderLoading, mutate: sendReminder } = useCreateSurveyResponseReminderMutation({ clientId: 'reviews' })
const {
  loading: bulkReminderLoading,
  mutate: sendBulkReminder,
  onDone: onDoneSendBulkReminder,
} = useCreateSurveyResponseRemindersMutation({ clientId: 'reviews' })

const loading = computed(() => singleReminderLoading.value || bulkReminderLoading.value)

const campaignsVariables = computed(() => ({
  productId: props.product.id,
  statuses: [IncentivizationCampaignStatusEnum.Active],
}))

const { result: campaignsResult } = useReviewsIncentivizationCampaignsQuery(campaignsVariables, { clientId: 'reviews' })

type PersonalizationOption = {
  hash: string
  locale: string
  inviteMessage: string
}

type CampaignGroup = {
  id: string
  title: string
  personalizations: PersonalizationOption[]
}

const campaignGroups = computed<CampaignGroup[]>(() => {
  const campaigns = campaignsResult.value?.manage?.incentivizationCampaigns ?? []
  return campaigns
    .map((campaign) => ({
      id: campaign.id ?? '',
      title: campaign.title ?? '',
      personalizations: (campaign.personalizations ?? [])
        .filter((p): p is typeof p & { hashValue: string } => !!p.hashValue)
        .map((p) => ({
          hash: p.hashValue,
          locale: p.locale.toUpperCase(),
          inviteMessage: p.inviteMessage,
        })),
    }))
    .filter((group) => group.personalizations.length > 0)
})

const hasCampaignOptions = computed(() => campaignGroups.value.length > 0)

const selectedCampaignHash = ref<string | null>(null)

type SelectedEntry = {
  group: CampaignGroup
  personalization: PersonalizationOption
}

const selectedEntry = computed<SelectedEntry | null>(() => {
  if (!selectedCampaignHash.value) return null
  for (const group of campaignGroups.value) {
    const personalization = group.personalizations.find((p) => p.hash === selectedCampaignHash.value)
    if (personalization) return { group, personalization }
  }
  return null
})

const selectedPersonalization = computed<PersonalizationOption | null>(() => selectedEntry.value?.personalization ?? null)

const selectedCampaignTitle = computed<string>(() => selectedEntry.value?.group.title ?? '')

function onCampaignSelectionChange(value: string | string[] | undefined) {
  selectedCampaignHash.value = typeof value === 'string' ? value : null
}

const reviewLink = computed(() => {
  if (props.hasOldReview) {
    return `<a href="${REVIEW_LINK_BE_PLACEHOLDER}">${t('Reviews.sendReminderDialog.hasOldReview.reviewLinkText')}</a> `
  }
  if (props.isBulkReminder) {
    return `<a href="${REVIEW_LINK_BE_PLACEHOLDER}">${t('Reviews.sendReminderDialog.bulkReminder.reviewLinkText')}</a> `
  }
  return `<a href="${REVIEW_LINK_BE_PLACEHOLDER}">${t('Reviews.sendReminderDialog.reviewLinkText')}</a> `
})

const sendReminderDialogTexts = computed(() => {
  if (props.isBulkReminder) {
    return {
      modalTitle: t('Reviews.sendReminderDialog.bulkReminder.title'),
      modalDescription: t('Reviews.sendReminderDialog.bulkReminder.text'),
      messageBody: t('Reviews.sendReminderDialog.bulkReminder.messageBody', {
        productTitle: props.product.title,
        vendorName: me.value?.partnerCompany?.displayName || '',
        reviewLink: reviewLink.value,
      }).toString(),
    }
  }
  if (state) {
    switch (state.value) {
      case SurveyResponseState.Rejected:
        return {
          modalTitle: t('Reviews.sendReminderDialog.title'),
          modalDescription: t('Reviews.sendReminderDialog.rejected.text'),
          messageBody: t('Reviews.sendReminderDialog.rejected.messageBody', {
            authorName: reviewerFirstName.value || t('Reviews.sendReminderDialog.salutationFallbackMessageBody'),
            productTitle: props.product.title,
            vendorName: me.value?.partnerCompany?.displayName || '',
            reviewLink: reviewLink.value,
          }).toString(),
        }
      case SurveyResponseState.Published:
        if (props.hasOldReview) {
          return {
            modalTitle: t('Reviews.sendReminderDialog.hasOldReviewTitle'),
            modalDescription: t('Reviews.sendReminderDialog.hasOldReview.text', {
              authorName: reviewerFirstName.value || t('Reviews.sendReminderDialog.salutationFallbackMessageBody'),
            }),
            messageBody: t('Reviews.sendReminderDialog.hasOldReview.messageBody', {
              authorName: reviewerFirstName.value || t('Reviews.sendReminderDialog.salutationFallbackMessageBody'),
              productTitle: props.product.title,
              vendorName: me.value?.partnerCompany?.displayName || '',
              reviewLink: reviewLink.value,
            }),
          }
        }
        return
      default:
        return {
          modalTitle: t('Reviews.sendReminderDialog.title'),
          modalDescription: t('Reviews.sendReminderDialog.text'),
          messageBody: t('Reviews.sendReminderDialog.messageBody', {
            authorName: reviewerFirstName.value || t('Reviews.sendReminderDialog.salutationFallbackMessageBody'),
            productTitle: props.product.title,
            vendorName: me.value?.partnerCompany?.displayName || '',
            reviewLink: reviewLink.value,
          }).toString(),
        }
    }
  }
  return null
})

const messageBannerTexts = computed(() => {
  if (props.surveyResponse && state) {
    return getMessageBannerForReviewState(
      state.value,
      reviewerFirstName.value || '',
      props.surveyResponse.rejectedReason,
      true,
      props.hasOldReview,
    )
  }
  return null
})

const formData = reactive<FormData>({
  message: sendReminderDialogTexts.value?.messageBody || '',
  copyToSender: false,
})

const containsLink = withI18nMessage(containsValidator, {
  withArguments: true,
  messagePath: () => 'Reviews.sendReminderDialog.missingReviewLink',
})

const rules = computed<ValidationArgs<FormData>>(() => ({
  message: {
    required,
    minLength: minLength(MSG_MIN_LENGTH),
    maxLength: maxLength(MSG_MAX_LENGTH),
    contains: containsLink(REVIEW_LINK_BE_PLACEHOLDER),
  },
  copyToSender: {},
}))

const v$ = useVuelidate(rules, formData)

function insertLink() {
  richInput.value?.insertHTML(REVIEW_LINK_PLACEHOLDER)
  v$.value.message.$model = v$.value.message.$model.replace(REVIEW_LINK_PLACEHOLDER, reviewLink.value)
  // RichInput does not allow outside model change so we have to rerender component
  inputKey.value = inputKey.value + 1
}

function open() {
  modal.value?.open()
}

function close() {
  modal.value?.close()
}

function onFocusIn() {
  focused.value = true
}

function onFocusOut() {
  setTimeout(() => (focused.value = false), 100)
}

async function onSubmit() {
  const isFormValid = await v$.value.$validate()
  if (!isFormValid) {
    showNotification(t('Reviews.sendReminderDialog.invalidForm'), 'error')
    return
  }

  const turnDown = new TurnDown({
    emDelimiter: '_',
    linkStyle: 'inlined',
    headingStyle: 'atx',
  })

  const campaignHash = selectedCampaignHash.value || undefined

  try {
    if (props.surveyResponse && !props.isBulkReminder) {
      track('review_remind', {
        surface: 'editor',
        reminder_type: reminderType.value,
        entity_type: 'tool',
        entity_id: props.product.id,
        scope: 'single',
        survey_response_id: props.surveyResponse.id,
      })
      await sendReminder({
        input: {
          surveyResponseId: props.surveyResponse.id,
          message: turnDown.turndown(nl2br(formData.message)),
          sendCopyToVendor: formData.copyToSender,
          campaignHash,
        },
      })
      showNotification(t('Reviews.sendReminderDialog.success', { authorName: reviewerFirstName.value }), 'success', 10000)
    } else {
      track('review_remind', {
        surface: 'editor',
        reminder_type: reminderType.value,
        entity_type: 'tool',
        entity_id: props.product.id,
        scope: 'bulk',
      })
      await sendBulkReminder({
        input: {
          productSlug: props.product.slug,
          message: turnDown.turndown(nl2br(formData.message)),
          sendCopyToVendor: formData.copyToSender,
          filters: props.filters,
          campaignHash,
        },
      })
    }
    close()
  } catch (_e) {
    showNotification(t('Reviews.sendReminderDialog.serverError'), 'error', 10000)
  }
}

onDoneSendBulkReminder(({ data }) => {
  if (!data?.manage.createSurveyResponseReminders) {
    return
  }
  const { successCount, failedCount } = data.manage.createSurveyResponseReminders
  if (successCount > 0) {
    showNotification(t('Reviews.sendBulkReminderDialog.success', { counter: successCount }), 'success', 10000)
  } else if (failedCount) {
    showNotification(t('Reviews.sendBulkReminderDialog.failure', { counter: failedCount }), 'error', 10000)
  }
})

defineExpose({ open, close })
</script>
